import { isPositivePurchasePrice } from './inventoryInputs';

const count = value => Math.max(0, Number(value) || 0);
const trim = value => String(value ?? '').trim();
const matches = (expected, scanned) => {
  if (expected.productId != null) return String(expected.productId) === String(scanned.productId);
  if (trim(expected.barcode)) return trim(expected.barcode) === trim(scanned.barcode);
  return (
    Boolean(trim(expected.productCode)) && trim(expected.productCode) === trim(scanned.productCode)
  );
};

/** Mirrors the server: posted rows own their scans; each remaining unit is assigned once. */
export function allocateIncomingScans(expected = [], scanned = []) {
  const rows = expected
    .filter(Boolean)
    .filter(row => count(row.quantity) > 0)
    .sort((a, b) => a.id - b.id);
  const scans = scanned.filter(Boolean).sort((a, b) => a.id - b.id);
  const remaining = scans.map(row => count(row.quantity));
  const allocated = new Map();
  const consume = row => {
    let assigned = 0;
    for (let i = 0; i < scans.length && assigned < count(row.quantity); i++) {
      if (!matches(row, scans[i])) continue;
      const take = Math.min(remaining[i], count(row.quantity) - assigned);
      remaining[i] -= take;
      assigned += take;
    }
    allocated.set(row.id, assigned);
  };
  rows.filter(row => row.status === 'posted').forEach(consume);
  rows.filter(row => row.status !== 'posted').forEach(consume);
  scans.forEach((scan, i) => {
    if (!remaining[i]) return;
    const target = [...rows].reverse().find(row => row.status !== 'posted' && matches(row, scan));
    if (target) {
      allocated.set(target.id, allocated.get(target.id) + remaining[i]);
      remaining[i] = 0;
    }
  });
  const summary = {
    expectedCount: rows.length,
    full: 0,
    partial: 0,
    over: 0,
    expectedQtyTotal: 0,
    scannedQtyMatchedTotal: 0,
    unmatchedQuantity: remaining.reduce((sum, quantity) => sum + quantity, 0),
  };
  rows.forEach(row => {
    const quantity = count(row.quantity),
      assigned = allocated.get(row.id) || 0;
    summary.expectedQtyTotal += quantity;
    summary.scannedQtyMatchedTotal += Math.min(quantity, assigned);
    if (assigned > quantity) summary.over++;
    else if (assigned === quantity) summary.full++;
    else if (assigned > 0) summary.partial++;
  });
  return { allocated, summary };
}

export function incomingBulkReadiness(expected = [], allocation) {
  const pending = expected.filter(row => row && count(row.quantity) > 0 && row.status !== 'posted');
  let reason = '';
  if (allocation.summary.unmatchedQuantity > 0)
    reason = 'Є невраховані скани. Зіставте їх з очікуваними товарами.';
  else if (
    pending.some(
      row =>
        row.status !== 'scanned_full' || allocation.allocated.get(row.id) !== count(row.quantity),
    )
  )
    reason = 'Усі позиції мають бути повністю проскановані без перескану.';
  else if (pending.some(row => row.productId == null))
    reason = 'Прив’яжіть усі позиції до товарів каталогу.';
  else if (pending.some(row => !isPositivePurchasePrice(row.purchasePrice)))
    reason = 'Вкажіть додатну ціну закупки для кожної позиції.';
  return {
    show: pending.length > 0,
    count: pending.length,
    canPost: pending.length > 0 && !reason,
    reason,
  };
}
